import Image, { ImageLoaderProps, ImageProps } from 'next/image';
import { useEffect, useState } from 'react';
import { ASSET_PREFIX } from 'utils/constants';
import { resizeImageUrl } from 'utils/image/imageResizeService';

interface IImageWithFallbackProps extends ImageProps {
  /** The fallback image to load if the src cannot be loaded. Note that this is now optional, as a src that can be
   * handled by the image resizing service will provide WebP images automatically
   */
  fallbackSrc?: string;
}

/** An Image component that provides an optional fallback. This uses the image resizing service for images on the
 * whitelisted domains. See https://github.com/DAYMADEUK/lambda-image-transformer/blob/main/source/image-handler/image-request.ts#L343
 *
 * The image resizing service provides WebP images automatically, therefore the "fallback" part of this component is
 * not necessary any more for images that are served off the image resizing service
 */
const ImageWithFallback = ({
  src,
  fallbackSrc,
  width,
  ...rest
}: IImageWithFallbackProps) => {
  const [imgSrc, setImgSrc] = useState(src);

  const loader = ({ src, width, quality }: ImageLoaderProps) => {
    const urlWithProtocol = `${
      src.startsWith('https://') ? '' : ASSET_PREFIX
    }${src}`;

    // NextJS won't play ball with next.config.js's images.deviceSizes array of sizes. We don't want images bigger than
    // 2048, as we don't have enough traffic on desktop, especially not retina desktops (4k wide images)
    if (width > 2048) {
      width = 2048;
    }

    return resizeImageUrl(urlWithProtocol, { width, quality });
  };

  useEffect(() => {
    setImgSrc(src);
  }, [src]);

  return (
    <Image
      {...rest}
      width={width}
      loader={loader}
      src={
        // NextJS image component explodes horribly if the imgSrc is null, so
        // we must prevent that
        imgSrc ?? '/image-with-fallback-image-was-null.png'
      }
      onErrorCapture={() => {
        if (fallbackSrc) {
          setImgSrc(fallbackSrc);
        }
      }}
      onError={() => {
        if (fallbackSrc) {
          setImgSrc(fallbackSrc);
        }
      }}
    />
  );
};

export default ImageWithFallback;
